import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ServiceRequestFormData,
    ServiceStatus,
    OrganizationStatusSetting,
    ServiceLocationPreference,
    OrganizationData
} from '../../types';
import { PUBLIC_STATUS_OPTIONS } from '../../config/serviceStatuses';
import DiagnosticChat from '../DiagnosticChat';
import { useMachineData } from '../../hooks/useMachineData';
import { canAccessFeature } from '../../utils/tierPermissions';
import './StepStyles.scss';

interface Step5StatusProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationStatusSettings: OrganizationStatusSetting[] | undefined;
  organizationData: OrganizationData;
}

const baseStatusOptions = PUBLIC_STATUS_OPTIONS;

function Step5Status({
  formData,
  updateFormData,
  registerValidation,
  organizationStatusSettings = [], // Provide default empty array
  organizationData,
}: Step5StatusProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDiagnosticComplete, setIsDiagnosticComplete] = useState(false);

  const canRunDiagnostic = canAccessFeature(organizationData.organization.tier, 'diagnosticChat');

  const requiresDiagnostic = canRunDiagnostic &&
    formData.selectedStatus &&
    ['repair', 'repair_maintenance'].includes(formData.selectedStatus);

  // Fetch machines using the same hook as Step4Machine
  const { machines: availableMachines } =
    useMachineData(formData.selectedCategoryId ?? null, formData.selectedMakeId ?? null);

  // Get machine info for diagnostic
  const selectedMachine = useMemo(() => {
    if (!formData.selectedMachineId) return null;
    return availableMachines.find(m => m.id === formData.selectedMachineId);
  }, [formData.selectedMachineId, availableMachines]);

  const selectedMake = useMemo(() => {
    if (!selectedMachine?.make_id) return null;
    return organizationData.allMakes.find(m => m.id === selectedMachine.make_id);
  }, [selectedMachine, organizationData.allMakes]);

  // Reset diagnostic completion when status changes away from repair
  useEffect(() => {
    if (!requiresDiagnostic) {
      setIsDiagnosticComplete(false);
      // Clear diagnostic data when not needed
      if (formData.diagnosticConversationId || formData.diagnosticQuoteId) {
        updateFormData({
          diagnosticConversationId: null,
          diagnosticQuoteId: null
        });
      }
    }
  }, [requiresDiagnostic, formData.diagnosticConversationId, formData.diagnosticQuoteId, updateFormData]);

  const availableStatusOptions = useMemo(() => {
    const offeredStatuses = new Map<ServiceStatus, ServiceLocationPreference>();

    if (organizationStatusSettings && organizationStatusSettings.length > 0) {
      organizationStatusSettings.forEach(setting => {
          const statusValue = setting.status_value as ServiceStatus;
          offeredStatuses.set(statusValue, setting.service_location);
      });
    }

    const filtered = baseStatusOptions
        .filter(option => offeredStatuses.has(option.value))
        .map(option => ({
            ...option,
            locationPreference: offeredStatuses.get(option.value) as ServiceLocationPreference
        }));

    return filtered;
  }, [organizationStatusSettings]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const statusValue = e.target.value as ServiceStatus;
    const newPreference = availableStatusOptions.find(opt => opt.value === statusValue)?.locationPreference ?? null;

    const requiresShop = newPreference === 'shop' || newPreference === 'both';
    const updates: Partial<ServiceRequestFormData> = {
        selectedStatus: statusValue,
        shopServicePreference: requiresShop ? formData.shopServicePreference : null
    };

    // Reset request location when status changes
    if (newPreference === 'both') {
        // When both are available, make user choose
        updates.requestLocation = null;
    } else if (newPreference === 'shop') {
        // When only shop is available, set to shop automatically
        updates.requestLocation = 'shop';
    } else if (newPreference === 'home') {
        // When only home is available, set to home automatically
        updates.requestLocation = 'home';
    } else {
        updates.requestLocation = null;
    }

    updateFormData(updates);
    setValidationError(null);
  };

  const handleShopPreferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const shopPref = e.target.value as 'drop_off' | 'pickup';
      updateFormData({ shopServicePreference: shopPref });
      setValidationError(null);
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const location = e.target.value as 'home' | 'shop';
      updateFormData({ requestLocation: location });
      setValidationError(null);
  };

  const handleLocationNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData({ locationNotes: e.target.value });
  };

  const handleDiagnosticComplete = useCallback((conversationId: string, quoteId: number | null) => {
    setIsDiagnosticComplete(true);
    updateFormData({
      diagnosticConversationId: conversationId,
      diagnosticQuoteId: quoteId
    });
    setValidationError(null);
  }, [updateFormData]);

  const validateStep = useCallback(() => {
    if (!formData.selectedStatus) {
      setValidationError("Please select the reason for your service request.");
      return false;
    }

    // Check if diagnostic is required and complete for professional tier repairs
    if (requiresDiagnostic && !isDiagnosticComplete) {
      setValidationError("Please complete the diagnostic conversation about your repair.");
      return false;
    }

    // Only check location/shop preferences if diagnostic is complete (for repairs) or not needed
    if (!requiresDiagnostic || isDiagnosticComplete) {
      // Add null check for organizationStatusSettings
      if (organizationStatusSettings) {
        const preference = organizationStatusSettings.find(s => s.status_value === formData.selectedStatus)?.service_location;

        // Validate shop preference if service location is shop or both
        if ((preference === 'shop' || preference === 'both') &&
            formData.requestLocation === 'shop' &&
            !formData.shopServicePreference) {
                setValidationError("Please specify if you will drop off the equipment or require pickup.");
                return false;
        }

        // Validate service location selection if both options are available
        if (preference === 'both' && !formData.requestLocation) {
          setValidationError("Please select where you would like the service to be performed.");
          return false;
        }
      }
    }

    setValidationError(null);
    return true;
  }, [formData.selectedStatus, formData.shopServicePreference, formData.requestLocation, organizationStatusSettings, requiresDiagnostic, isDiagnosticComplete]);

  useEffect(() => { registerValidation(validateStep); }, [registerValidation, validateStep]);

  const getLocationPreferenceText = (preference: ServiceLocationPreference | null | undefined): string => {
      if (!preference) return '';
      switch (preference) {
          case 'shop': return '(Service at Shop Only)';
          case 'home': return '(Service at Your Location Only)';
          case 'both': return '(Service at Shop or Your Location)';
          default: return '';
      }
  }

  return (
    <div className="form-step step-status">
      <h2>Reason for Service</h2>
      <p className="step-intro">
        Select the option that best describes why you need service. Availability may vary based on location.
      </p>
  
      {validationError && <div className="error-message summary-error">{validationError}</div>}
  
      <div className="form-group">
        <div className="radio-group status-options">
          {availableStatusOptions.length > 0 ? (
            availableStatusOptions.map((option) => (
              <React.Fragment key={option.value}>
                <div className="radio-option">
                  <input
                    type="radio"
                    id={`status-${option.value}`}
                    name="selectedStatus"
                    value={option.value}
                    checked={formData.selectedStatus === option.value}
                    onChange={handleStatusChange}
                    required
                  />
                  <label htmlFor={`status-${option.value}`}>
                    {option.label}
                    <span className="location-preference-text"> {getLocationPreferenceText(option.locationPreference)}</span>
                    {formData.selectedStatus === option.value && isDiagnosticComplete && requiresDiagnostic && (
                      <span className="diagnostic-complete-badge">
                        <svg className="checkmark-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <span className="badge-text">Diagnostic Complete</span>
                      </span>
                    )}
                  </label>
                </div>

                {/* Show diagnostic chat for professional tier repairs */}
                {(() => {
                  const isSelectedStatus = formData.selectedStatus === option.value;
                  const needsDiagnostic = requiresDiagnostic && !isDiagnosticComplete;
                  const hasRequiredInfo = selectedMachine && selectedMake;
                  const shouldShow = isSelectedStatus && needsDiagnostic && hasRequiredInfo;

                  // Show diagnostic chat when all conditions are met
                  if (shouldShow) {
                    return (
                      <div className="form-group sub-options-group diagnostic-group">
                        <DiagnosticChat
                          organizationId={organizationData.organization.id.toString()}
                          customerId={formData.customerId}
                          machineId={selectedMachine.id}
                          make={selectedMake.name}
                          model={selectedMachine.name}
                          onComplete={handleDiagnosticComplete}
                        />
                      </div>
                    );
                  }

                  // Show error message if diagnostic is needed but machine/make info is missing
                  if (isSelectedStatus && needsDiagnostic && !hasRequiredInfo) {
                    return (
                      <div className="form-group sub-options-group">
                        <div className="error-message">
                          <p><strong>Machine information required:</strong></p>
                          <p>Please go back to the previous step and ensure you have selected a category, make, and machine for your equipment.</p>
                          {!formData.selectedMachineId && <p className="missing-info">• Missing: Machine selection</p>}
                          {formData.selectedMachineId && !selectedMachine && <p className="missing-info">• Machine data could not be loaded</p>}
                          {selectedMachine && !selectedMachine.make_id && <p className="missing-info">• Selected machine is missing make information</p>}
                          {selectedMachine && selectedMachine.make_id && !selectedMake && <p className="missing-info">• Make information could not be loaded</p>}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })()}

                {/* Show location selection immediately after selected status if applicable (only after diagnostic for repairs) */}
                {formData.selectedStatus === option.value &&
                  option.locationPreference === 'both' &&
                  (!requiresDiagnostic || isDiagnosticComplete) && (
                  <div className="form-group sub-options-group">
                    <label className="required sub-options-label">Service Location Preference</label>
                    <div className="radio-group location-preference-options">
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="location-home"
                          name="requestLocation"
                          value="home"
                          checked={formData.requestLocation === 'home'}
                          onChange={handleLocationChange}
                          required={true}
                        />
                        <label htmlFor="location-home">Perform service at my location</label>
                      </div>
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="location-shop"
                          name="requestLocation"
                          value="shop"
                          checked={formData.requestLocation === 'shop'}
                          onChange={handleLocationChange}
                          required={true}
                        />
                        <label htmlFor="location-shop">Perform service at the shop</label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show location notes field for at-home service (only after diagnostic for repairs) */}
                {formData.selectedStatus === option.value &&
                  formData.requestLocation === 'home' &&
                  (!requiresDiagnostic || isDiagnosticComplete) && (
                  <div className="form-group sub-options-group">
                    <label className="sub-options-label">
                      How can we find your equipment?
                      <span className="label-hint">
                        {' '}(Optional - e.g., "in the backyard behind the shed")
                      </span>
                    </label>
                    <textarea
                      className="form-textarea"
                      value={formData.locationNotes || ''}
                      onChange={handleLocationNotesChange}
                      placeholder="Describe where your equipment is located (optional)"
                      rows={2}
                    />
                  </div>
                )}

                {/* Show shop preference immediately after if service at shop is needed (only after diagnostic for repairs) */}
                {formData.selectedStatus === option.value &&
                  ((option.locationPreference === 'shop') ||
                   (option.locationPreference === 'both' && formData.requestLocation === 'shop')) &&
                  (!requiresDiagnostic || isDiagnosticComplete) && (
                  <div className="form-group sub-options-group">
                    <label className="required sub-options-label">Shop Service Preference</label>
                    <div className="radio-group shop-preference-options">
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="shopPref-drop_off"
                          name="shopServicePreference"
                          value="drop_off"
                          checked={formData.shopServicePreference === 'drop_off'}
                          onChange={handleShopPreferenceChange}
                          required={true}
                        />
                        <label htmlFor="shopPref-drop_off">I will bring the equipment to the shop</label>
                      </div>
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="shopPref-pickup"
                          name="shopServicePreference"
                          value="pickup"
                          checked={formData.shopServicePreference === 'pickup'}
                          onChange={handleShopPreferenceChange}
                          required={true}
                        />
                        <label htmlFor="shopPref-pickup">I need the shop to pick up the equipment</label>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))
          ) : (
            <p className="info-message">No service reasons are currently configured for this organization. Please contact them directly.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Step5Status;